/**
 * VARIANT HIDING SYSTEM
 *
 * Standalone module for hiding specific product variants
 * Runs immediately on page load, independent of Loop widget or sale features
 *
 * Location: assets/variant-hiding.js
 * Loaded in: sections/product-loop-subscriptions.liquid
 */

/* ========================= CONFIGURATION ========================= */

/**
 * FALLBACK CONFIG (used only if metafield not set)
 *
 * RECOMMENDED: Use metafields instead of editing this file
 * - Go to Shopify Admin → Products → Edit Product
 * - Find "Hide Variants" metafield
 * - Add variant titles to hide (e.g., "2 Pack", "4 Pack", "6 Pack")
 *
 * This hardcoded config is only used as a fallback for products
 * that don't have the metafield set.
 */
const VARIANT_HIDE_CONFIG = {
  // Example: '9037080395991': ['2 Pack', '4 Pack', '6 Pack']
};

/* ========================= METAFIELD READING ========================= */

/**
 * Read variant hiding configuration from metafield (preferred) or fallback config
 * @param {string} productId - The product ID
 * @returns {Array|null} Array of variant titles to hide, or null
 */
function getVariantsToHide(productId) {
  // Priority 1: Check for metafield configuration
  const configElement = document.getElementById('variant-hide-config');
  if (configElement) {
    try {
      const config = JSON.parse(configElement.textContent);
      if (config.productId == productId && config.variantsToHide) {
        console.log('[Variant Hiding] Using metafield configuration for product:', productId);
        console.log('[Variant Hiding] Variants to hide from metafield:', config.variantsToHide);
        return config.variantsToHide;
      }
    } catch (e) {
      console.warn('[Variant Hiding] Failed to parse metafield config:', e);
    }
  }

  // Priority 2: Fallback to hardcoded config
  if (VARIANT_HIDE_CONFIG[productId]) {
    console.log('[Variant Hiding] Using hardcoded configuration for product:', productId);
    console.log('[Variant Hiding] Variants to hide from hardcoded config:', VARIANT_HIDE_CONFIG[productId]);
    return VARIANT_HIDE_CONFIG[productId];
  }

  // No configuration found
  return null;
}

/* ========================= HIDING LOGIC ========================= */

/**
 * Hide specific variants for configured products
 * @param {string} productId - Optional product ID, auto-detected if not provided
 */
function hideProductVariants(productId) {
  // 1. Get product ID (auto-detect if not provided)
  if (!productId) {
    const productInfoEl = document.querySelector('[data-product-id]');
    productId = productInfoEl?.getAttribute('data-product-id');
  }

  if (!productId) {
    console.log('[Variant Hiding] No product ID found');
    return;
  }

  // 2. Get variants to hide (metafield first, then fallback)
  const variantsToHide = getVariantsToHide(productId);

  if (!variantsToHide || variantsToHide.length === 0) {
    console.log('[Variant Hiding] No variants configured to hide for product:', productId);
    return;
  }

  console.log('[Variant Hiding] Hiding variants for product', productId, ':', variantsToHide);

  // 3. Find ALL variant inputs (supports multiple selector patterns)
  const variantSelectors = [
    'input[type="radio"][name^="Amount-"]',           // Custom pattern
    'input[type="radio"][name^="options["]',          // Standard Shopify
    'input[type="radio"][name^="option-"]',           // Alternative pattern
    'input[type="radio"][data-variant-option-value]'  // Data attribute pattern
  ];

  let hiddenCount = 0;

  // 4. Loop through each selector pattern
  variantSelectors.forEach(selector => {
    const variantInputs = document.querySelectorAll(selector);

    // 5. For each matching input
    variantInputs.forEach(input => {
      const variantValue = input.value.trim();

      // 6. Check if this variant should be hidden
      if (variantsToHide.includes(variantValue)) {

        // Hide the radio input
        input.style.display = 'none';
        input.setAttribute('data-variant-hidden', 'true');

        // Hide the label (next sibling method)
        const label = input.nextElementSibling;
        if (label && label.tagName === 'LABEL') {
          label.style.display = 'none';
          label.setAttribute('data-variant-hidden', 'true');
        }

        // Hide label by 'for' attribute (if different from next sibling)
        if (input.id) {
          const labelByFor = document.querySelector(`label[for="${input.id}"]`);
          if (labelByFor && labelByFor !== label) {
            labelByFor.style.display = 'none';
            labelByFor.setAttribute('data-variant-hidden', 'true');
          }
        }

        // Hide individual wrapper div (like .swatch__option) but NOT the fieldset
        const wrapper = input.closest('.swatch__option, [data-variant-option-value-wrapper]');
        if (wrapper && !wrapper.matches('fieldset, .product-form__input')) {
          wrapper.style.display = 'none';
          wrapper.setAttribute('data-variant-hidden', 'true');
        }

        console.log('[Variant Hiding] Hidden variant:', variantValue);
        hiddenCount++;
      }
    });
  });

  console.log(`[Variant Hiding] Successfully hidden ${hiddenCount} variant(s)`);
}

/* ========================= INITIALIZATION ========================= */

// Run immediately when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    hideProductVariants();
  });
} else {
  // DOM already loaded, run now
  hideProductVariants();
}

// Expose globally for Loop widget to re-hide on dynamic changes
window.hideProductVariants = hideProductVariants;

console.log('[Variant Hiding] Module loaded and ready');
